import { useState, useEffect } from 'react'
import { getPublishedResumes, deletePublishedResume } from '../utils/api'
import ApplicationPackage from './ApplicationPackage'

const C = {
  bg:        '#05050d',
  surface:   'rgba(255,255,255,0.028)',
  surface2:  'rgba(255,255,255,0.05)',
  border:    'rgba(180,185,230,0.09)',
  borderHi:  'rgba(180,185,230,0.22)',
  accent:    '#b8bdd4',
  text:      '#dce0f0',
  textMuted: '#8890aa',
  textDim:   '#6a6e85',
  green:     '#4dc88a',
  orange:    '#c8964d',
  red:       '#c85a5a',
}
const font = {
  heading: "'Cormorant Garamond', serif",
  mono:    "'Space Mono', monospace",
  body:    "'Inter', sans-serif",
}

function formatDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function RefererList({ referers }) {
  const entries = Object.entries(referers || {}).sort((a, b) => b[1] - a[1])
  if (!entries.length) return <span style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim }}>нет данных</span>
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
      {entries.map(([domain, count]) => (
        <span key={domain} style={{
          fontFamily: font.mono, fontSize: 9, padding: '2px 8px', borderRadius: 6,
          background: 'rgba(184,189,212,0.08)', border: `1px solid ${C.border}`,
          color: C.textMuted,
        }}>
          {domain} · {count}
        </span>
      ))}
    </div>
  )
}

export default function PublishedResumesList({ userId }) {
  const [list, setList]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [openSlug, setOpenSlug] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [copiedSlug, setCopiedSlug] = useState(null)

  async function load() {
    setLoading(true)
    try {
      const data = await getPublishedResumes(userId)
      setList(Array.isArray(data) ? data : [])
    } catch {
      setList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [userId])

  async function handleDelete(slug) {
    if (!window.confirm('Удалить публикацию? Ссылка перестанет работать.')) return
    setDeleting(slug)
    try {
      await deletePublishedResume(slug, userId)
      setList(l => l.filter(r => r.slug !== slug))
      if (openSlug === slug) setOpenSlug(null)
    } catch (e) {
      alert(e.message)
    } finally {
      setDeleting(null)
    }
  }

  function copyLink(slug) {
    const url = `${window.location.origin}/r/${slug}`
    navigator.clipboard?.writeText(url).catch(() => {})
    setCopiedSlug(slug)
    setTimeout(() => setCopiedSlug(null), 2000)
  }

  if (loading) return (
    <div style={{ padding: '32px 0', textAlign: 'center', fontFamily: font.mono, fontSize: 11, color: C.textDim, letterSpacing: '0.1em' }}>
      загрузка...
    </div>
  )

  if (!list?.length) return (
    <div style={{ padding: '40px 0', textAlign: 'center' }}>
      <div style={{ fontFamily: font.heading, fontSize: 28, color: C.textDim, marginBottom: 8 }}>Нет публикаций</div>
      <div style={{ fontFamily: font.body, fontSize: 13, color: C.textDim }}>
        Сформируйте пакет отклика на вакансию и нажмите «Опубликовать»
      </div>
    </div>
  )

  return (
    <div>
      <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 20 }}>
        Опубликованные резюме · {list.length}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {list.map(item => {
          const url = `${window.location.origin}/r/${item.slug}`
          const isOpen = openSlug === item.slug
          return (
            <div key={item.slug} style={{
              borderRadius: 14, border: `1px solid ${isOpen ? C.borderHi : C.border}`,
              background: C.surface, overflow: 'hidden',
            }}>
              {/* ── Card header ── */}
              <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontFamily: font.heading, fontSize: 20, color: C.accent }}>
                      {item.vacancy?.company || '—'}
                    </span>
                    <span style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted }}>
                      {item.vacancy?.title || ''}
                    </span>
                  </div>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim, textDecoration: 'none', display: 'block', marginBottom: 10, wordBreak: 'break-all' }}
                  >
                    {url}
                  </a>

                  {/* Stats row */}
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim, letterSpacing: '0.1em', marginBottom: 2 }}>
                        ПРОСМОТРЫ
                      </div>
                      <div style={{ fontFamily: font.heading, fontSize: 24, color: item.visit_count > 0 ? C.accent : C.textDim, lineHeight: 1 }}>
                        {item.visit_count}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim, letterSpacing: '0.1em', marginBottom: 4 }}>
                        ИСТОЧНИКИ
                      </div>
                      <RefererList referers={item.referers} />
                    </div>
                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                      <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim }}>
                        {formatDate(item.created_at)}
                      </div>
                      {item.updated_at !== item.created_at && (
                        <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim, marginTop: 2 }}>
                          обновлено {formatDate(item.updated_at)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => copyLink(item.slug)}
                    style={{
                      fontFamily: font.mono, fontSize: 9, padding: '5px 12px', borderRadius: 8,
                      border: `1px solid ${copiedSlug === item.slug ? C.green + '60' : C.border}`,
                      background: 'transparent', color: copiedSlug === item.slug ? C.green : C.textDim,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {copiedSlug === item.slug ? '✓ скопировано' : 'копировать ссылку'}
                  </button>
                  <button
                    onClick={() => setOpenSlug(isOpen ? null : item.slug)}
                    style={{
                      fontFamily: font.mono, fontSize: 9, padding: '5px 12px', borderRadius: 8,
                      border: `1px solid ${isOpen ? C.borderHi : C.border}`,
                      background: isOpen ? 'rgba(184,189,212,0.06)' : 'transparent',
                      color: isOpen ? C.accent : C.textDim, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {isOpen ? '▲ свернуть' : '✎ редактировать'}
                  </button>
                  <button
                    onClick={() => handleDelete(item.slug)}
                    disabled={deleting === item.slug}
                    style={{
                      fontFamily: font.mono, fontSize: 9, padding: '5px 12px', borderRadius: 8,
                      border: `1px solid ${C.red}40`,
                      background: 'transparent', color: C.red + 'cc', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {deleting === item.slug ? '...' : '✕ удалить'}
                  </button>
                </div>
              </div>

              {/* ── Expanded edit view ── */}
              {isOpen && (
                <div style={{ borderTop: `1px solid ${C.border}`, padding: '0 20px' }}>
                  <ApplicationPackage
                    vacancy={item.vacancy}
                    userId={userId}
                    initialSlug={item.slug}
                    onClose={() => setOpenSlug(null)}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
